import { Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MicButtonProps {
  isSupported: boolean;
  disabled?: boolean;
  onStart: () => void;
}

/**
 * Idle-state trigger only — shown next to the textarea when nothing is being
 * recorded. Once recording starts, `MessageInput` swaps the whole row for
 * `VoiceRecordingBar`, so this component never needs to render a "stop" state.
 * Hidden (not disabled) when unsupported: a disabled button with no explanation
 * is worse than no button, per the design doc's error-handling section.
 *
 * Press-and-hold (start on press, stop on release) was tried and reverted the
 * same day: the mic permission prompt is a native OS dialog outside the page,
 * so granting it requires lifting the finger off the button — which looked
 * identical to a deliberate release and stopped the session before permission
 * was even granted. Plain tap-to-start it is; see `voice-input-ui-preferences`
 * project memory for the full story before re-attempting a hold gesture here.
 */
export const MicButton = ({ isSupported, disabled = false, onStart }: MicButtonProps) => {
  if (!isSupported) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Start voice input"
      disabled={disabled}
      onClick={onStart}
      // A permanent fill (not just ghost's hover-only background) so this reads
      // as a button of the same visual weight as the filled Send button next to
      // it — matches Claude's own mobile app mic button. Both buttons measure
      // the same 40x40/44x44 box (verified), but `slate-100` was too pale
      // against a white page — low contrast made the circle's edge hard to see,
      // so it read as smaller than Send's darker circle even at equal size.
      // `slate-200` has enough contrast to read as the same size.
      className="h-10 w-10 md:h-11 md:w-11 shrink-0 rounded-full cursor-pointer bg-slate-200 hover:bg-slate-300"
    >
      {/* `size-*`, not `h-* w-*`: the Button component forces any svg lacking a
          class containing "size-" down to a fixed 16px (see button.tsx's
          `[&_svg:not([class*='size-'])]:size-4`) — h-5/w-5 here was silently
          overridden and never actually rendered bigger than 16px. */}
      <Mic className="size-5 md:size-4" />
    </Button>
  );
};
